import { CheckCircleFilled, LoadingOutlined, SendOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type SetupPortal as Portal,
  useRefreshBaaStatusMutation,
  useResendBaaMutation,
  useSetBaaRecipientMutation,
} from "../../service/implementation.service";

const { Text } = Typography;

type Props = {
  token: string;
  portal: Portal;
  /** True when DocuSign has bounced the customer back to us after signing. */
  justReturned: boolean;
  onReturnHandled: () => void;
};

/**
 * Step 1 — issue and sign the BAA.
 *
 * The customer tells us who the agreement should be addressed to, and DocuSign
 * emails that person. Once they sign, DocuSign emails Nudge to counter-sign, and
 * the step completes when the envelope is fully executed. Deliberately not
 * embedded signing: the person with authority to sign a HIPAA agreement is often
 * not whoever is looking at this page.
 *
 * Three states after sending — waiting on their signer, waiting on Nudge, done —
 * plus a way to correct the address, because with no support address on the page a
 * typo here would otherwise dead-end the whole onboarding.
 */
export default function BaaStep({ token, portal, justReturned, onReturnHandled }: Props) {
  const [setRecipient, { isLoading: isSending }] = useSetBaaRecipientMutation();
  const [resend, { isLoading: isResending }] = useResendBaaMutation();
  const [refreshStatus, { isLoading: isRefreshing }] = useRefreshBaaStatusMutation();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const handledReturnRef = useRef(false);

  const { baa } = portal;
  const isSigned = !!baa.signed_at;
  const awaitingNudge = !isSigned && !!baa.customer_signed_at;
  const isSent = !isSigned && !awaitingNudge && !!baa.sent_at;

  // DocuSign can bounce a signer back here. Ask DocuSign directly rather than
  // making them sit through Connect latency.
  useEffect(() => {
    if (!justReturned || handledReturnRef.current) return;
    handledReturnRef.current = true;
    (async () => {
      try {
        await refreshStatus({ token }).unwrap();
      } catch {
        // The webhook remains the backstop.
      } finally {
        onReturnHandled();
      }
    })();
  }, [justReturned, token, refreshStatus, onReturnHandled]);

  const send = useCallback(
    async (values: { name: string; email: string }) => {
      setError(null);
      setNotice(null);
      try {
        await setRecipient({ token, ...values }).unwrap();
        setIsEditing(false);
      } catch (err) {
        const code = (err as { data?: { code?: string } })?.data?.code;
        if (code === "not_configured") {
          setError("We're still preparing your BAA — we'll email it to you shortly.");
          return;
        }
        setError("We couldn't send the agreement just now. Please try again in a moment.");
      }
    },
    [setRecipient, token],
  );

  const handleResend = useCallback(async () => {
    setError(null);
    try {
      await resend({ token }).unwrap();
      setNotice("Sent again — it can take a minute to arrive, and it may land in spam.");
    } catch {
      setError("We couldn't resend it just now. Please try again in a moment.");
    }
  }, [resend, token]);

  const handleCheck = useCallback(async () => {
    setError(null);
    setNotice(null);
    try {
      const updated = await refreshStatus({ token }).unwrap();
      if (!updated.baa.signed_at && !updated.baa.customer_signed_at) {
        setNotice("DocuSign still shows it as unsigned. It updates the moment they sign.");
      }
    } catch {
      setError("We couldn't reach DocuSign just now. Please try again in a moment.");
    }
  }, [refreshStatus, token]);

  if (isSigned) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleFilled />}
        message="Your BAA is signed by both sides and on file. Nothing else needed here."
      />
    );
  }

  if (awaitingNudge) {
    return (
      <Alert
        type="info"
        showIcon
        icon={<LoadingOutlined />}
        message={`Signed by ${baa.signer_name || "your signer"} — thank you.`}
        description={`It's with ${baa.countersigner_name} to counter-sign. This step completes on its own, and we'll email you when it does.`}
      />
    );
  }

  if (isSent && !isEditing) {
    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {error ? <Alert type="warning" showIcon message={error} /> : null}
        {notice ? <Alert type="info" showIcon message={notice} /> : null}
        <Alert
          type="warning"
          showIcon
          message={`Sent to ${baa.signer_name} at ${baa.signer_email}`}
          description={`They'll get an email from DocuSign with the agreement. ${baa.countersigner_name} has already signed it, so theirs is the last signature needed — this step completes on its own once they sign.`}
        />
        <Space wrap>
          <Button loading={isRefreshing} onClick={handleCheck}>
            They've signed it — check now
          </Button>
          <Button type="link" loading={isResending} onClick={handleResend}>
            Resend the email
          </Button>
          <Button type="link" onClick={() => setIsEditing(true)}>
            Wrong person or address?
          </Button>
        </Space>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 8 }} /> : null}

      {isEditing ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          We'll withdraw the agreement we already sent and issue a fresh one to this person.
        </Text>
      ) : null}

      <Form
        layout="vertical"
        requiredMark={false}
        initialValues={{ name: baa.suggested_name, email: baa.suggested_email }}
        onFinish={send}
        style={{ marginTop: 12 }}
      >
        <Form.Item
          name="name"
          label="Who should sign it?"
          rules={[{ required: true, message: "We need a name for the agreement." }]}
        >
          <Input placeholder="Full name" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Their email address"
          rules={[
            { required: true, message: "We need an address to send it to." },
            { type: "email", message: "That doesn't look like an email address." },
          ]}
        >
          <Input placeholder="name@yourpractice.com" autoComplete="off" inputMode="email" />
        </Form.Item>
        <Space wrap>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={isSending}>
            {isEditing ? "Re-issue the BAA" : "Send the BAA"}
          </Button>
          {isEditing ? (
            <Button type="link" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          ) : null}
        </Space>
      </Form>

      <Text type="secondary" style={{ fontSize: 12, marginTop: 10, display: "block" }}>
        {baa.countersigner_name} counter-signs after them, and this step completes on its own — you don't need to tell
        us.
      </Text>
    </Space>
  );
}
